// The phone note lane for instruments (bass, harmony, lead): a small piano roll whose rows are only
// the day's scale notes (so every note is in key), one bar or half a bar at a time with big cells.
// Tap an empty cell to add a note of the chosen length (or a whole chord in chord mode); tap a note
// to remove it (a chord goes as one). Each tap is one undo step.
import { useEffect, useMemo, useRef, useState } from "react";
import { LOOP_BARS, pitchClass, scaleNotesInRange, slotRange, TICKS_PER_BAR, type NoteEvent } from "@eb/shared";
import { noteLabel } from "./gridMath";
import { addNote, removeNote } from "./ops";
import { previewSound } from "./padInput";
import { edit, useEditor, useSongKey, useView } from "./store";

const STEP = TICKS_PER_BAR / 16;
const LENGTHS = [
  { ticks: STEP, label: "1/16" },
  { ticks: 2 * STEP, label: "1/8" },
  { ticks: 4 * STEP, label: "1/4" },
  { ticks: 8 * STEP, label: "1/2" },
  { ticks: 16 * STEP, label: "1 bar" },
] as const;
const ROLE_NAME = ["Bass", "Harmony", "Lead", "Classics", "Atmos & FX"];

export function NoteLane({ slot }: { slot: number }) {
  const def = useEditor((s) => s.def);
  const readOnly = useEditor((s) => s.readOnly);
  const loop = useView();
  const [bar, setBar] = useState(0);
  const [half, setHalf] = useState<0 | 1 | null>(0); // null = the whole bar
  const [length, setLength] = useState<number>(slot === 17 ? 8 * STEP : 2 * STEP);
  const [chords, setChords] = useState(slot === 17);
  const scroller = useRef<HTMLDivElement>(null);

  // Defaults per instrument: chords get long notes and chord mode.
  useEffect(() => {
    setLength(slot === 17 ? 8 * STEP : 2 * STEP);
    setChords(slot === 17);
  }, [slot]);

  const songKey = useSongKey();
  const notes = useMemo(() => {
    const r = slotRange(slot);
    return r && songKey ? scaleNotesInRange(songKey, r.lo, r.hi) : [];
  }, [slot, songKey]);
  const rows = useMemo(() => [...notes].reverse(), [notes]); // high notes on top

  const steps = half === null ? 16 : 8;
  const from = bar * TICKS_PER_BAR + (half === 1 ? 8 * STEP : 0);
  const to = from + steps * STEP;

  // Notes of this instrument that sound inside the view.
  const visible = loop.events.filter((e) => e.pad === slot && e.tick < to && e.tick + e.length_ticks > from);

  // Scroll the lane to where the notes are (or the middle) when the instrument changes.
  useEffect(() => {
    const el = scroller.current;
    if (!el || rows.length === 0) return;
    const mine = loop.events.filter((e) => e.pad === slot);
    const target = mine.length ? rows.indexOf(mine[Math.floor(mine.length / 2)].note) : Math.floor(rows.length / 2);
    const rowH = el.scrollHeight / rows.length;
    el.scrollTop = Math.max(0, (target + 0.5) * rowH - el.clientHeight / 2);
    // Only on instrument changes, not on every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot, rows]);

  if (!def || !songKey || notes.length === 0) return null;
  const key = songKey;

  const chordOf = (note: number) => {
    const i = notes.indexOf(note);
    return [notes[i], notes[i + 2], notes[i + 4]].filter((n): n is number => n !== undefined);
  };

  const add = (note: number, tick: number) => {
    const len = Math.min(length, LOOP_BARS * TICKS_PER_BAR - tick);
    const pitches = chords ? chordOf(note) : [note];
    edit((l) =>
      pitches.reduce((acc, n) => addNote(acc, { tick, pad: slot, note: n, velocity: 100, length_ticks: len }, key), l),
    );
    for (const n of pitches) previewSound(slot, n, 100);
  };

  const remove = (e: NoteEvent) => {
    // In chord mode a tap removes everything of this instrument starting with it (the chord).
    const targets = chords ? loop.events.filter((x) => x.pad === slot && x.tick === e.tick) : [e];
    edit((l) => targets.reduce((acc, t) => removeNote(acc, t), l));
  };

  return (
    <section className="note-lane" aria-label={`${ROLE_NAME[slot - 16]} notes`} data-testid="note-lane">
      <div className="lane-controls">
        <div className="phone-bars" role="radiogroup" aria-label="Bar">
          {Array.from({ length: LOOP_BARS }, (_, b) => (
            <button
              key={b}
              type="button"
              role="radio"
              aria-checked={bar === b}
              className={`chip-btn${bar === b ? " on" : ""}${loop.events.some((e) => e.pad === slot && Math.floor(e.tick / TICKS_PER_BAR) === b) ? " has" : ""}`}
              onClick={() => setBar(b)}
              data-testid={`lane-bar-${b}`}
            >
              Bar {b + 1}
            </button>
          ))}
        </div>
        <div className="phone-bars" role="radiogroup" aria-label="View">
          {(
            [
              [0, "Beats 1–2"],
              [1, "Beats 3–4"],
              [null, "Whole bar"],
            ] as const
          ).map(([h, label]) => (
            <button
              key={label}
              type="button"
              role="radio"
              aria-checked={half === h}
              className={`chip-btn${half === h ? " on" : ""}`}
              onClick={() => setHalf(h)}
              data-testid={`lane-view-${h ?? "bar"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="phone-bars" role="radiogroup" aria-label="Note length">
          {LENGTHS.map((l) => (
            <button
              key={l.ticks}
              type="button"
              role="radio"
              aria-checked={length === l.ticks}
              className={`chip-btn${length === l.ticks ? " on" : ""}`}
              onClick={() => setLength(l.ticks)}
              data-testid={`lane-len-${l.ticks}`}
            >
              {l.label}
            </button>
          ))}
          <label className="chip-btn lane-chords">
            <input
              type="checkbox"
              checked={chords}
              onChange={(e) => setChords(e.target.checked)}
              data-testid="lane-chords"
            />{" "}
            Chords
          </label>
        </div>
      </div>
      <div className="lane-scroll" ref={scroller}>
        <div
          className="lane-grid"
          style={{
            gridTemplateColumns: `44px repeat(${steps}, 1fr)`,
            gridTemplateRows: `repeat(${rows.length}, var(--lane-row))`,
          }}
          role="grid"
          aria-label={`Bar ${bar + 1}${half === null ? "" : half === 0 ? ", beats 1–2" : ", beats 3–4"}`}
        >
          {rows.map((n, r) => {
            const tonic = pitchClass(n) === pitchClass(key.tonic);
            return [
              <span
                key={`l${n}`}
                className={`lane-label${tonic ? " tonic" : ""}`}
                style={{ gridRow: r + 1, gridColumn: 1 }}
              >
                {noteLabel(n)}
              </span>,
              ...Array.from({ length: steps }, (_, k) => {
                const tick = from + k * STEP;
                return (
                  <button
                    key={`c${n}:${k}`}
                    type="button"
                    className={`lane-cell${k % 4 === 0 ? " beat" : ""}${tonic ? " tonic" : ""}`}
                    style={{ gridRow: r + 1, gridColumn: k + 2 }}
                    aria-label={`Add ${noteLabel(n)} at step ${(tick % TICKS_PER_BAR) / STEP + 1}`}
                    disabled={readOnly}
                    onClick={() => add(n, tick)}
                    data-testid={`cell-${n}-${k}`}
                  />
                );
              }),
            ];
          })}
          {visible.map((e) => {
            const r = rows.indexOf(e.note);
            if (r < 0) return null;
            const s0 = Math.max(0, Math.floor((e.tick - from) / STEP));
            const s1 = Math.min(steps, Math.ceil((e.tick + e.length_ticks - from) / STEP));
            const starts = e.tick >= from;
            return (
              <button
                key={`n${e.note}:${e.tick}`}
                type="button"
                className={`lane-note${starts ? "" : " continues"}`}
                style={{ gridRow: r + 1, gridColumn: `${s0 + 2} / span ${Math.max(1, s1 - s0)}` }}
                aria-label={`Remove ${noteLabel(e.note)}${chords ? " (chord)" : ""}`}
                disabled={readOnly}
                onClick={() => remove(e)}
                data-testid={`note-${e.note}-${e.tick}`}
              >
                {starts && <span>{noteLabel(e.note)}</span>}
              </button>
            );
          })}
        </div>
      </div>
      <p className="muted small lane-hint">
        Tap a cell to add a note{chords ? " (a chord from the scale)" : ""}; tap a note to remove it. Every row is in
        the day's key.
      </p>
    </section>
  );
}
